import React, { useCallback, useMemo, useState } from 'react';
import { observer } from 'mobx-react';
import { useDispatch } from 'react-redux';
import { generatePath } from 'react-router';
import Accordion from '@material-ui/core/ExpansionPanel';
import AccordionDetails from '@material-ui/core/ExpansionPanelDetails';
import AccordionSummary from '@material-ui/core/ExpansionPanelSummary';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import TextField from '@material-ui/core/TextField';

import { translate } from 'services/i18nService';
import NotificationService from 'services/NotificationService';
import twoFAService, { twoFATypes } from 'services/twoFA';
import UserStore from 'stores/UserStore';
import { actionCreators as userActions } from 'pages/User/State';
import { ENUM_USER_TYPE } from 'enum/profile';
import { privateRoutes } from 'enum/routeDefinitions';
import Button from 'components/Button/Button';
import Modal from 'components/Modal/Modal';

import styles from './EmailModal.css';


const EmailModal = () => {
  const { accountType, email, username } = UserStore.user;

  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(false);

  const settingsLink = useMemo(() => {
    const link = {
      pathname: generatePath(privateRoutes.settings.account.emails, { username }),
      state: { from2FAConfirmModal: true },
    };

    if (accountType === ENUM_USER_TYPE.STUDIO_OWNER) {
      link.pathname = generatePath(privateRoutes.studio.settings.root, { username });
    }

    return link;
  }, [accountType, username]);

  const onConfirm = useCallback(async() => {
    setIsLoading(true);

    const { data } = await twoFAService.enable(twoFATypes.email, { username });

    setIsLoading(false);

    if (!data || data.status === 'FAILED') {
      NotificationService.create({
        type: NotificationService.type.TOAST,
        subtype: NotificationService.subtype.ERROR,
        text: translate('account.twoFa.serverError'),
      });

      return;
    }

    NotificationService.create({
      type: NotificationService.type.TOAST,
      text: translate('account.emails.twoFAEnabled'),
      subtype: NotificationService.subtype.SUCCESS,
    });

    dispatch(userActions.getUserState());
  }, [dispatch, username]);

  return (
    <Modal
      className={styles.modal}
      disableBackdropClick={true}
      show={true}
      showClose={false}
      size="medium"
    >
      <h3>
        {translate('profile.manage.twoFa.email')}
      </h3>

      <p>
        {translate('profile.manage.twoFa.confirmModal.mainSection')}
      </p>

      <Accordion classes={{ root: styles.detailsWrapper }} >
        <AccordionSummary
          classes={{ expanded: styles.detailsTitle }}
          expandIcon={<ExpandMoreIcon />}
        >
          {translate('profile.manage.twoFa.confirmModal.details.title')}
        </AccordionSummary>

        <AccordionDetails>
          {translate('profile.manage.twoFa.confirmModal.details.content')}
        </AccordionDetails>
      </Accordion>

      <TextField
        InputLabelProps={{ shrink: true }}
        disabled={true}
        fullWidth={true}
        inputProps={{ className: styles.email }}
        size="medium"
        value={email}
        variant="outlined"
      />

      <div className={styles.footer}>
        <Button colour="orange" disabled={isLoading} onClick={onConfirm}>
          {translate('account.twoFa.confirm')}
        </Button>

        <Button link={settingsLink}>
          {translate('account.twoFa.changeTwoFa')}
        </Button>
      </div>
    </Modal>
  );
};

export default observer(EmailModal);
